import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { logmarToSnellen } from '../../../helpers/data';
import { colors, fonts, radii, breakpoints, Button } from '../../../ui';

// Scoring-step chart (Figma session/10 right: logMAR + letters list; session/21 left, landscape:
// Selection | logMAR | Snellen | Characters table). The current line is highlighted; its letters
// can be tapped to point at one on the patient's screen (highlightLogmarCharacter message).

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  height: 100%;
  width: 100%;
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: ${radii.button}px;
  background: ${({ variant }) => (variant === 'list' ? colors.slate[50] : '#fff')};
  box-shadow: 0 0 0 1px ${colors.slate[200]};
  -webkit-overflow-scrolling: touch;
`;

const T = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${fonts.family};
  font-size: 13px;
  color: ${colors.slate[800]};
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 8px 6px;
    background: ${colors.slate[100]};
    color: ${colors.slate[600]};
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    border-bottom: 1px solid ${colors.slate[200]};
  }
  th + th,
  td + td {
    border-left: ${({ variant }) => (variant === 'table' ? `1px solid ${colors.slate[200]}` : '0')};
  }
  td {
    height: ${({ variant }) => (variant === 'table' ? 30 : 34)}px;
    padding: 0 6px;
    text-align: center;
    border-bottom: 1px solid ${({ variant }) => (variant === 'table' ? colors.slate[200] : 'transparent')};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  tr.dim td {
    color: ${colors.slate[400]};
  }
  tr.current td {
    background: ${colors.slate[200]};
    color: ${colors.slate[900]};
    font-weight: 700;
  }
  .chars {
    font-family: 'OpticianSans', ${fonts.family};
    font-size: 14px;
    letter-spacing: 0.1em;
  }
  .chars span,
  .chars button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 24px;
    margin: 0 1px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    font: inherit;
    color: inherit;
  }
  .chars button {
    cursor: pointer;
    &:hover {
      background: ${colors.brand[100]};
    }
    &[aria-pressed='true'] {
      background: ${colors.primary};
      color: #fff;
    }
    &:focus-visible {
      outline: 2px solid ${colors.brand[300]};
    }
  }
  .dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1.5px solid ${colors.slate[400]};
    box-sizing: border-box;
    vertical-align: middle;
  }
  tr.current .dot {
    border-color: ${colors.teal};
    background: radial-gradient(${colors.teal} 0 3px, #fff 3.5px);
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    td {
      height: 30px;
    }
    .chars button,
    .chars span {
      min-width: 18px;
    }
  }
`;

/**
 * @param data                logmarData rows ({ id, value })
 * @param currentId           id of the line being shown to the patient
 * @param characters          { [logmarId]: 'Z K N C R' } (testContext.logmarCharacters)
 * @param highlighted         id of the highlighted character element (or '' / null)
 * @param onHighlight(charId) tap on a letter of the current line; null clears
 * @param variant             'list' (portrait: logMAR + letters) | 'table' (landscape: + Selection, Snellen)
 */
export const LogmarChart = ({ data, currentId, characters, highlighted, onHighlight, variant = 'list' }) => {
  const currentRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    const box = scrollRef.current;
    const row = currentRef.current;
    if (!box || !row) return;
    const top = row.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTop = Math.max(0, top - box.clientHeight / 2 + row.offsetHeight / 2);
  }, [currentId]);
  const table = variant === 'table';
  return (
    <Wrap>
      <Scroll variant={variant} ref={scrollRef}>
        <T variant={variant} aria-label='logMAR chart'>
          <thead>
            <tr>
              {table && <th scope='col'>Selection</th>}
              <th scope='col'>logMAR</th>
              {table && <th scope='col'>Snellen</th>}
              <th scope='col'>Characters</th>
            </tr>
          </thead>
          <tbody>
            {data.map((logmar) => {
              const current = logmar.id === currentId;
              const chars = characters?.[logmar.id]?.split(' ') || [];
              return (
                <tr key={logmar.id} className={current ? 'current' : currentId && logmar.id < currentId ? 'dim' : undefined} ref={current ? currentRef : null} aria-current={current ? 'step' : undefined}>
                  {table && (
                    <td>
                      <span className='dot' aria-hidden='true' />
                    </td>
                  )}
                  <td>{logmar.value.toFixed(1)}</td>
                  {table && <td>{logmarToSnellen[logmar.value.toFixed(1)]}</td>}
                  <td className='chars'>
                    {chars.map((char) => {
                      const id = `logmar-id-${logmar.id}-char-${char}`;
                      return current ? (
                        <button key={id} id={id} type='button' aria-pressed={highlighted === id} aria-label={`Point at ${char}`} onClick={() => onHighlight(id)}>
                          {char}
                        </button>
                      ) : (
                        <span key={id}>{char}</span>
                      );
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </T>
      </Scroll>
      {highlighted && (
        <Button variant='secondary' size='sm' onClick={() => onHighlight(null)} style={{ alignSelf: 'flex-start' }}>
          Clear highlight
        </Button>
      )}
    </Wrap>
  );
};

export default LogmarChart;
